import React, { useState } from 'react';
import QuillEditor from './editors/QuillEditor';
import TinyMCEEditor from './editors/TinyMCEEditor';
import CKEditor5 from './editors/CKEditor5';
import EditorJSComponent from './editors/EditorJSComponent';
import { processHtmlForPreview } from '../services/htmlProcessor';

function ContentEditor({ initialContent, onContentChange, title }) {
  const [isEditing, setIsEditing] = useState(false);
  const [selectedEditor, setSelectedEditor] = useState(null);
  const [editedContent, setEditedContent] = useState(initialContent);

  const handleContentChange = (content) => {
    setEditedContent(content);
    if (onContentChange) {
      onContentChange(content);
    }
  };

  const handleEditClick = (editorType) => {
    setSelectedEditor(editorType);
    setIsEditing(true);
  };

  const handlePreview = () => {
    setIsEditing(false);
  };

  const editorButtons = [
    { name: 'Quill', type: 'quill', color: '#1E88E5', icon: '✒️' },
    { name: 'TinyMCE', type: 'tinymce', color: '#FF6B35', icon: '📝' },
    { name: 'CKEditor', type: 'ckeditor', color: '#0287D0', icon: '✍️' },
    { name: 'Editor.js', type: 'editorjs', color: '#2B3137', icon: '📄' }
  ];

  return (
    <div style={{ border: '1px solid #ddd', borderRadius: '8px', overflow: 'hidden' }}>
      {/* Header with editor selection buttons */}
      <div style={{ 
        background: '#f5f5f5', 
        padding: '15px', 
        borderBottom: '1px solid #ddd'
      }}>
        <div style={{ 
          display: 'flex', 
          justifyContent: 'space-between', 
          alignItems: 'center',
          flexWrap: 'wrap',
          gap: '10px'
        }}>
          <div style={{ fontSize: '0.9em', color: '#666', fontWeight: 'bold' }}>
            {isEditing ? (
              <>
                {selectedEditor === 'quill' && '✒️ Editing with Quill'}
                {selectedEditor === 'tinymce' && '📝 Editing with TinyMCE'}
                {selectedEditor === 'ckeditor' && '✍️ Editing with CKEditor'}
                {selectedEditor === 'editorjs' && '📄 Editing with Editor.js'}
              </>
            ) : (
              '👁️ Preview Mode - Choose an editor to start editing'
            )}
          </div>

          <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
            {!isEditing ? (
              <>
                {editorButtons.map(editor => (
                  <button
                    key={editor.type}
                    onClick={() => handleEditClick(editor.type)}
                    style={{
                      background: editor.color,
                      color: 'white',
                      border: 'none',
                      borderRadius: '4px',
                      padding: '8px 16px',
                      cursor: 'pointer',
                      fontSize: '0.85em',
                      fontWeight: '500',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '6px',
                      transition: 'opacity 0.2s'
                    }}
                    onMouseOver={(e) => e.currentTarget.style.opacity = '0.9'}
                    onMouseOut={(e) => e.currentTarget.style.opacity = '1'}
                  >
                    <span>{editor.icon}</span>
                    <span>Edit with {editor.name}</span>
                  </button>
                ))}
              </>
            ) : (
              <button
                onClick={handlePreview}
                style={{
                  background: '#4CAF50',
                  color: 'white',
                  border: 'none',
                  borderRadius: '4px',
                  padding: '8px 16px',
                  cursor: 'pointer',
                  fontSize: '0.85em',
                  fontWeight: '500'
                }}
              >
                👁️ Preview
              </button>
            )}
          </div>
        </div>

        {!isEditing && (
          <div style={{ 
            marginTop: '10px', 
            padding: '10px', 
            background: '#e3f2fd', 
            borderRadius: '4px',
            fontSize: '0.8em',
            color: '#0277BD'
          }}>
            💡 <strong>Choose your preferred editor:</strong> Each editor has unique features. 
            Quill is fast and lightweight, TinyMCE has extensive plugins, CKEditor is feature-rich, 
            and Editor.js uses a block-based approach.
          </div>
        )}
      </div>
      
      {/* Editor/Preview Area */}
      {isEditing ? (
        <div style={{ minHeight: '600px' }}>
          {selectedEditor === 'quill' && (
            <QuillEditor
              initialContent={editedContent || initialContent}
              onContentChange={handleContentChange}
              title={title}
            />
          )}
          {selectedEditor === 'tinymce' && (
            <TinyMCEEditor
              initialContent={editedContent || initialContent}
              onContentChange={handleContentChange}
              title={title}
            />
          )}
          {selectedEditor === 'ckeditor' && (
            <CKEditor5
              initialContent={editedContent || initialContent}
              onContentChange={handleContentChange}
              title={title}
            />
          )}
          {selectedEditor === 'editorjs' && (
            <EditorJSComponent
              initialContent={editedContent || initialContent}
              onContentChange={handleContentChange}
              title={title}
            />
          )}
        </div>
      ) : (
        <iframe
          srcDoc={processHtmlForPreview(`
            <!DOCTYPE html>
            <html>
            <head>
              <meta charset="UTF-8">
              <meta name="viewport" content="width=device-width, initial-scale=1.0">
              <style>
                /* Unified content styles for consistent preview */
                body {
                  font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Oxygen, Ubuntu, Cantarell, sans-serif;
                  font-size: 16px;
                  line-height: 1.6;
                  color: #333;
                  padding: 20px;
                  margin: 0;
                  max-width: 100%;
                }
                img {
                  max-width: 100%;
                  height: auto;
                  display: block;
                  margin: 1.5em auto;
                }
                h1, h2, h3, h4, h5, h6 {
                  font-weight: 700;
                  line-height: 1.3;
                  margin-top: 1.5em;
                  margin-bottom: 0.5em;
                  color: #1a1a1a;
                }
                /* CRITICAL: Preserve all inline styles */
                [style*="text-align: center"],
                [style*="text-align:center"] {
                  text-align: center !important;
                }
                [style*="text-align: right"],
                [style*="text-align:right"] {
                  text-align: right !important;
                }
                * {
                  box-sizing: border-box;
                }
              </style>
            </head>
            <body>
              ${editedContent || initialContent}
            </body>
            </html>
          `)}
          title={title || "Content Preview"}
          sandbox="allow-same-origin allow-popups"
          style={{ 
            width: '100%', 
            height: '600px', 
            border: 'none',
            background: 'white'
          }}
        />
      )}
    </div>
  );
}

export default ContentEditor;
